import { lazy, type ReactElement, Suspense } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Appello } from './allenamenti/Appello.js';
import { ComposizioneSeduta } from './allenamenti/ComposizioneSeduta.js';
import { ElencoAllenamenti } from './allenamenti/ElencoAllenamenti.js';
import { LogSeduta } from './allenamenti/LogSeduta.js';
import { PaginaAllenamento } from './allenamenti/PaginaAllenamento.js';
import { SchedaAllenamento } from './allenamenti/SchedaAllenamento.js';
import { VotiAllenamento } from './allenamenti/VotiAllenamento.js';
import { Conversazioni, PaginaConversazione } from './assistente/Conversazioni.js';
import { ProviderAssistente } from './assistente/ProviderAssistente.js';
import { Account } from './amministrazione/Account.js';
import { CatalogoAttributi } from './amministrazione/CatalogoAttributi.js';
import { DatiSocieta } from './amministrazione/DatiSocieta.js';
import { InvitaAccount } from './amministrazione/InvitaAccount.js';
import { Registro, RegistroPiattaforma } from './amministrazione/Registro.js';
import { RuoliAccount } from './amministrazione/RuoliAccount.js';
import { Squadre } from './amministrazione/Squadre.js';
import { SquadreStagione } from './amministrazione/SquadreStagione.js';
import { PaginaCruscotto } from './cruscotto/PaginaCruscotto.js';
import { Staff } from './amministrazione/Staff.js';
import { Stagioni } from './amministrazione/Stagioni.js';
import { PaginaAccesso } from './accesso/PaginaAccesso.js';
import { PaginaInvito } from './accesso/PaginaInvito.js';
import { PaginaRecupero } from './accesso/PaginaRecupero.js';
import { PaginaReimposta } from './accesso/PaginaReimposta.js';
import { Competizioni } from './competizioni/Competizioni.js';
import { ElencoGiocatori } from './giocatori/ElencoGiocatori.js';
import { ImportRosa } from './giocatori/ImportRosa.js';
import { NuovoGiocatore } from './giocatori/NuovoGiocatore.js';
import { Rosa } from './giocatori/Rosa.js';
import { SchedaGiocatore } from './giocatori/SchedaGiocatore.js';
import { useSessione } from './accesso/useSessione.js';
import { Guscio } from './navigazione/Guscio.js';
import { GuscioSocieta } from './navigazione/GuscioSocieta.js';
import { NonTrovata } from './navigazione/NonTrovata.js';
import { ProviderContesto } from './navigazione/ProviderContesto.js';
import { ElencoEventi } from './partite/ElencoEventi.js';
import { ElencoPartite } from './partite/ElencoPartite.js';
import { SchedaCronaca } from './partite/SchedaCronaca.js';
import { SchedaInfo } from './partite/SchedaInfo.js';
import { CaricoPartita } from './carichi/CaricoPartita.js';
import { SchedaTabellino } from './partite/Tabellino.js';
import { VotiPartita } from './partite/VotiPartita.js';
import { ImpostazioniSquadra } from './squadre/ImpostazioniSquadra.js';
import { DettaglioScheda } from './schede/DettaglioScheda.js';
import { ElencoSchede } from './schede/ElencoSchede.js';
import { DettaglioTest } from './test-misura/DettaglioTest.js';
import { ElencoTest } from './test-misura/ElencoTest.js';
import { EsecuzioneTest } from './test-misura/EsecuzioneTest.js';
import { CarichiSquadra } from './carichi/CarichiSquadra.js';
import { Ingresso, SceltaContesto } from './squadre/SceltaContesto.js';
import { ClassificheGiocatori } from './statistiche/ClassificheGiocatori.js';
import { StatistichePresenze } from './statistiche/StatistichePresenze.js';
import { StatisticheSquadra } from './statistiche/StatisticheSquadra.js';
import { AccountPiattaforma } from './piattaforma/AccountPiattaforma.js';
import { ElencoSocieta } from './piattaforma/ElencoSocieta.js';
import { SchedaSocieta } from './piattaforma/SchedaSocieta.js';
import { PaginaPartita } from './timer/PaginaPartita.js';
import { AttributiSquadra } from './valutazioni/AttributiSquadra.js';
import './base.css';

// Recharts pesa quanto il resto dell'app: si scarica solo aprendo il confronto.
const Confronto = lazy(() =>
  import('./valutazioni/Confronto.js').then((m) => ({ default: m.Confronto })),
);

/** Rimanda a /accesso ricordando da dove si veniva, per tornarci dopo il login. */
function VaiAdAccesso() {
  const location = useLocation();
  return <Navigate to="/accesso" state={{ da: location.pathname }} replace />;
}

function RottaAccesso({
  autenticato,
  ricarica,
}: {
  autenticato: boolean;
  ricarica: () => Promise<void>;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const destinazione = (location.state as { da?: string } | null)?.da ?? '/';

  if (autenticato) return <Navigate to={destinazione} replace />;

  return (
    <PaginaAccesso
      onAccesso={async () => {
        await ricarica();
        navigate(destinazione, { replace: true });
      }}
    />
  );
}

function VaiRegistro() {
  const { ss, id } = useParams<{ ss: string; id: string }>();
  return <Navigate to={`/sq/${ss}/allenamenti/${id}/registro`} replace />;
}

export function App() {
  const { stato, ricarica, esci } = useSessione();

  if (stato.fase === 'caricamento') return <p className="attesa">Carico…</p>;

  const autenticato = stato.fase === 'autenticato';
  const protetta = (elemento: ReactElement) => (autenticato ? elemento : <VaiAdAccesso />);

  const rotte = (
    <Routes>
      <Route
        path="/accesso"
        element={<RottaAccesso autenticato={autenticato} ricarica={ricarica} />}
      />
      {/* Pubbliche anche con una sessione aperta: il link arriva via email. */}
      <Route path="/password-dimenticata" element={<PaginaRecupero />} />
      <Route path="/reimposta/:codice" element={<PaginaReimposta />} />
      <Route path="/invito/:codice" element={<PaginaInvito />} />
      <Route path="/" element={protetta(<Ingresso />)} />
      <Route path="/squadre" element={protetta(<SceltaContesto />)} />
      <Route path="/sq/:ss" element={protetta(<Guscio />)}>
        <Route index element={<PaginaCruscotto />} />
        <Route path="rosa" element={<Rosa />} />
        <Route path="rosa/import" element={<ImportRosa />} />
        <Route path="allenamenti" element={<ElencoAllenamenti />} />
        {/* Il guscio tiene testata e schede; cambiando scheda cambia solo il corpo. */}
        <Route path="allenamenti/:id" element={<PaginaAllenamento />}>
          <Route index element={<SchedaAllenamento />} />
          <Route path="blocchi" element={<ComposizioneSeduta />} />
          <Route path="registro" element={<LogSeduta />} />
          <Route path="presenze" element={<Appello />} />
          <Route path="voti" element={<VotiAllenamento />} />
        </Route>
        <Route path="allenamenti/:id/log" element={<VaiRegistro />} />
        <Route path="schede" element={<ElencoSchede />} />
        <Route path="schede/:id" element={<DettaglioScheda />} />
        <Route path="test" element={<ElencoTest />} />
        <Route path="test/:id" element={<DettaglioTest />} />
        <Route path="test/:id/esegui" element={<EsecuzioneTest />} />
        <Route path="test/:id/esecuzioni/:esecuzioneId" element={<EsecuzioneTest />} />
        <Route path="carichi" element={<CarichiSquadra />} />
        <Route path="partite" element={<ElencoPartite />} />
        <Route path="valutazioni/attributi" element={<AttributiSquadra />} />
        <Route
          path="valutazioni/confronto"
          element={
            <Suspense fallback={<p className="p-4 text-muted-foreground">Carico…</p>}>
              <Confronto />
            </Suspense>
          }
        />
        <Route path="statistiche" element={<StatisticheSquadra />} />
        <Route path="statistiche/giocatori" element={<ClassificheGiocatori />} />
        <Route path="statistiche/presenze" element={<StatistichePresenze />} />
        <Route path="impostazioni" element={<ImpostazioniSquadra />} />
        <Route path="staff" element={<Staff />} />
        <Route path="*" element={<NonTrovata />} />
      </Route>
      {/* Guscio di societa: anagrafiche e amministrazione non hanno una squadra (§1.1, §1.6). */}
      <Route path="/giocatori" element={protetta(<GuscioSocieta />)}>
        <Route index element={<ElencoGiocatori />} />
        <Route path="nuovo" element={<NuovoGiocatore />} />
        <Route path=":id" element={<SchedaGiocatore />} />
        <Route path=":id/tesseramenti" element={<SchedaGiocatore />} />
        <Route path=":id/attributi" element={<SchedaGiocatore />} />
        <Route path=":id/voti" element={<SchedaGiocatore />} />
        <Route path=":id/trend" element={<SchedaGiocatore />} />
        <Route path=":id/carichi" element={<SchedaGiocatore />} />
      </Route>
      <Route path="/competizioni" element={protetta(<GuscioSocieta />)}>
        <Route index element={<Competizioni />} />
      </Route>
      {/* Lo storico dell'assistente (V59): il pannello resta il modo
          principale di parlargli, questa è la vista per le conversazioni
          lunghe e per ritrovare le vecchie (§1.5). */}
      <Route path="/assistente" element={protetta(<GuscioSocieta />)}>
        <Route index element={<Conversazioni />} />
        <Route path="nuova" element={<PaginaConversazione />} />
        <Route path=":id" element={<PaginaConversazione />} />
      </Route>
      <Route path="/amministrazione" element={protetta(<GuscioSocieta />)}>
        <Route path="societa" element={<DatiSocieta />} />
        <Route path="stagioni" element={<Stagioni />} />
        <Route path="squadre" element={<Squadre />} />
        <Route path="squadre-stagione" element={<SquadreStagione />} />
        <Route path="account" element={<Account />} />
        <Route path="account/invita" element={<InvitaAccount />} />
        <Route path="account/:id/ruoli" element={<RuoliAccount />} />
        <Route path="attributi" element={<CatalogoAttributi />} />
        <Route path="registro" element={<Registro />} />
      </Route>
      {/* Piattaforma, del solo Super Admin: stesso guscio, senza società (§3.12). */}
      <Route path="/piattaforma" element={protetta(<GuscioSocieta />)}>
        <Route path="societa" element={<ElencoSocieta />} />
        <Route path="societa/:id" element={<SchedaSocieta />} />
        <Route path="account" element={<AccountPiattaforma />} />
        <Route path="registro" element={<RegistroPiattaforma />} />
      </Route>
      {/* Fuori dal guscio: la pagina lo indossa da sé fuori dal Live Timer,
          che è a schermo intero (§1.3). A partita conclusa tiene testata e
          schede; cambiando scheda cambia solo il corpo. */}
      <Route path="/sq/:ss/partite/:id" element={protetta(<PaginaPartita />)}>
        <Route index element={<SchedaInfo />} />
        <Route path="tabellino" element={<SchedaTabellino />} />
        <Route path="cronaca" element={<SchedaCronaca />} />
        <Route path="voti" element={<VotiPartita />} />
        <Route path="modifica" element={<ElencoEventi />} />
        <Route path="modifica/carico" element={<CaricoPartita />} />
        {/* Il vecchio percorso degli eventi porta alla modifica. */}
        <Route path="eventi" element={<Navigate to="../modifica" relative="path" replace />} />
      </Route>
      {/* Link stampati prima del prefisso di squadra: la pagina li riporta
          al percorso giusto una volta letta la partita. */}
      <Route path="/partite/:id" element={protetta(<PaginaPartita />)} />
      <Route path="*" element={protetta(<NonTrovata />)} />
    </Routes>
  );

  return (
    <BrowserRouter>
      {stato.fase === 'autenticato' ? (
        <ProviderContesto utente={stato.utente} esci={esci}>
          <ProviderAssistente>{rotte}</ProviderAssistente>
        </ProviderContesto>
      ) : (
        rotte
      )}
    </BrowserRouter>
  );
}
